import React, { useEffect, useRef, useState, useCallback } from "react";
import { Stack, Spinner, Text } from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";

const IframeComponent = () => {
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false); // Track if iframe has loaded

  const srcURL = process.env.REACT_APP_IFRAME_URL;

  /**
   * Function to safely parse and navigate based on incoming messages.
   */
  const handleMessage = useCallback(
    (event) => {
      try {
        // Ensure event is from trusted source
        if (!srcURL || event.origin !== new URL(srcURL).origin) {
          console.warn("Message from unauthorized origin:", event.origin);
          return;
        }

        // Validate expected message structure
        if (event.data && typeof event.data === "object" && event.data.type === "navigation") {
          const { url } = event.data;

          if (url && typeof url === "string") {
            try {
              const incomingURL = new URL(url, window.location.origin);
              const currentPath = location.pathname + location.search;
              
              // Transform URL: if pathname contains specific patterns, change to admin frontend routes
              let newPath = `${incomingURL.pathname}${incomingURL.search}`;
              
              // Check if the path contains '/library/' and transform it
              if (incomingURL.pathname.includes('/library/')) {
                const pathParts = incomingURL.pathname.split('/library/');
                newPath = `/libraries/${pathParts[1]}${incomingURL.search}`;
              }
              // Check if the path contains '/archive/' and transform it
              else if (incomingURL.pathname.includes('/archive/')) {
                const pathParts = incomingURL.pathname.split('/archive/');
                newPath = `/archive/${pathParts[1]}${incomingURL.search}`;
              }

              // Prevent unnecessary re-navigation
              if (newPath !== currentPath) {
                navigate(newPath, { replace: true });
              }
            } catch (error) {
              console.error("Invalid URL received in navigation message:", error);
            }
          }
        }
      } catch (error) {
        console.error("Error processing message:", error);
      }
    },
    [srcURL, location.pathname, location.search, navigate]
  );

  useEffect(() => {
    if (!srcURL) {
      setError("No iframe URL configured.");
      return;
    }

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage, srcURL]);

  useEffect(() => {
    const iframe = iframeRef.current;

    if (!iframe) return;

    const handleLoad = () => {
      setIsLoading(false);
      setIframeLoaded(true);
      setError(null);
    };

    const handleError = () => {
      setIsLoading(false);
      setError("Failed to load iframe content.");
    };

    iframe.addEventListener("load", handleLoad);
    iframe.addEventListener("error", handleError);

    return () => {
      iframe.removeEventListener("load", handleLoad);
      iframe.removeEventListener("error", handleError);
    };
  }, []);

  return (
    <Stack width="100%" height="100%" position="relative" border="1px solid #e2e8f0">
      {/* Loading State */}
      {isLoading && (
        <Stack position="absolute" top="0" left="0" width="100%" height="100%" justifyContent="center" alignItems="center" bg="rgba(255,255,255,0.7)" zIndex="10">
          <Spinner size="lg" />
          <Text fontSize="sm" color="gray.600">
            Loading iframe content...
          </Text>
        </Stack>
      )}

      {/* Error State */}
      {error && (
        <Stack width="100%" height="100%" justifyContent="center" alignItems="center" color="red.500">
          <Text fontSize="md">{error}</Text>
        </Stack>
      )}

      {/* Iframe */}
      {!error && (
        <iframe ref={iframeRef} src={srcURL} width="100%" height="100%" title="Tuli Research Centre for India Studies" style={{ border: "none", display: iframeLoaded ? "block" : "none" }} />
      )}
    </Stack>
  );
};

export default IframeComponent;